import { ReactNode } from 'react'
import { BooleanInput } from '../inputs/Boolean'
import { Slider } from '../inputs/Slider'
import { TextInput } from '../inputs/Text'
import { TextArea } from '../inputs/TextArea'
import { MultipleOptions } from '../inputs/MultipleOptions'
import { MultipleOptionsSingleSelect } from '../inputs/MultipleOptionsSingleSelect'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { format, parse } from 'date-fns'
import { DateInput } from '../inputs/DateInput'
import { ScreeningTitle } from './ScreeningTitle'
import { QuestionType, QuestionOption } from '@estercare/ester-shared'
import { ScreeningInfoSection } from './ScreeningInfoSection'
import { ScreeningHealthPlanRecommendations } from './ScreeningHealthPlanRecommendations'
import { DynamicScreeningComponentsProps } from './types'

export const DynamicScreeningComponents: Partial<
  Record<QuestionType, (props: DynamicScreeningComponentsProps) => ReactNode>
> = {
  multipleOption: ({ options, setAnswer, currentQuestion, title, description }) => {
    if (!options) return null

    const currentAnswers = currentQuestion?.answer || []

    const handleSelect = ({ id }: QuestionOption) => {
      const isSelected = currentAnswers.includes(id)

      if (id.includes('_negative')) {
        setAnswer(isSelected ? [] : [id])
        return
      }

      if (isSelected) {
        setAnswer(currentAnswers.filter((opt) => opt !== id))
      } else {
        setAnswer([...currentAnswers.filter((opt) => !opt.includes('_negative')), id])
      }
    }

    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <OptionsContainer>
          {options.map((option) => (
            <MultipleOptions
              key={option.id}
              options={[
                {
                  ...option,
                  isChecked: currentAnswers.includes(option.id),
                },
              ]}
              onSelect={handleSelect}
            />
          ))}
        </OptionsContainer>
      </>
    )
  },

  singleOption: ({ options, setAnswer, currentQuestion, title, description, metadata }) => {
    if (!options) return null
    const currentAnswers = currentQuestion?.answer || []

    return (
      <>
        <ScreeningTitle title={title} description={description} />
        {metadata?.healthPlanModule && <ScreeningHealthPlanRecommendations module={metadata.healthPlanModule} />}
        <SingleOptionsContainer>
          {options.map((option) => (
            <MultipleOptionsSingleSelect
              key={option.id}
              options={[
                {
                  ...option,
                  isSelected: currentAnswers.includes(option.id),
                },
              ]}
              setAnswer={(selectedOption) => setAnswer(selectedOption)}
            />
          ))}
        </SingleOptionsContainer>
      </>
    )
  },

  boolean: ({ options, answer, setAnswer, title, description }) => {
    if (!options) return null

    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <BooleanInput options={options} setAnswer={(newAnswer) => setAnswer(newAnswer)} answer={answer} />
      </>
    )
  },

  info: ({ metadata, description }) => {
    if (!metadata?.image) return null
    const { src, alt = '' } = metadata.image

    return <ScreeningInfoSection src={src} alt={alt} description={description} />
  },

  scale: ({ metadata, answer, setAnswer, title, description }) => {
    if (!metadata?.scale) return null

    const { min, max, minLabel, maxLabel, defaultValue } = metadata.scale
    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <Slider
          minValue={min}
          maxValue={max}
          minLabel={minLabel}
          maxLabel={maxLabel}
          defaultValue={defaultValue || min}
          answer={answer[0]}
          onChange={(newAnswer) => setAnswer([newAnswer])}
        />
      </>
    )
  },

  textInput: ({ metadata, answer, setAnswer, title, description, showValidation }) => {
    const errorMessage = showValidation ? metadata?.validation?.message : undefined

    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <TextInput
          value={answer[0] || ''}
          onChange={(e) => setAnswer([e.target.value])}
          placeholder={metadata?.textPlaceholder}
          type={metadata?.textInput?.type || 'text'}
          autoFocus={true}
          focusColor={theme.color.plumMid}
          error={errorMessage}
        />
      </>
    )
  },

  textInputMultipleLine: ({ metadata, answer, setAnswer, title, description }) => {
    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <TextArea
          value={answer[0] || ''}
          onChange={(newAnswer) => setAnswer([newAnswer.target.value])}
          placeholder={metadata?.textPlaceholder}
          autoFocus={true}
          focusColor={theme.color.plumMid}
        />
      </>
    )
  },

  date: ({ metadata, answer, setAnswer, title, description }) => {
    const currentDate = answer[0] ? parse(answer[0], 'yyyy-MM-dd', new Date()) : null
    return (
      <>
        <ScreeningTitle title={title} description={description} />
        <DateInput
          answer={currentDate}
          onChange={(newanswer) => newanswer && setAnswer([format(newanswer, 'yyyy-MM-dd')])}
          granularity={metadata?.dateGranularity || ['year', 'month', 'day']}
          disableFuture
        />
      </>
    )
  },
}

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.small}px;
  width: 100%;
  max-width: 620px;
  text-align: left;

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: 1fr;
    max-width: 330px;
    margin: 0 auto;
    text-align: center;
    gap: ${theme.spacing.medium}px;
    margin-bottom: ${theme.spacing.xlarge}px;
  }
`

const SingleOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  width: 100%;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    margin: 0 auto;
    margin-bottom: ${theme.spacing.xlarge}px;
  }
`
